import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ScrollText } from 'lucide-react';
import { listAuditLog, type AuditLogEntry } from '@/lib/api/audit-log.js';
import { listUsers } from '@/lib/api/users.js';
import { formatCurrency, formatDateTime, prettyLabel } from '@/lib/format.js';
import { Pagination } from '@/components/common/Pagination.js';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.js';

function overrideLine(meta: Record<string, unknown> | undefined): string | null {
  if (!meta) return null;
  const name =
    (typeof meta.approvedByName === 'string' && meta.approvedByName) ||
    (typeof meta.authorizedByName === 'string' && meta.authorizedByName) ||
    null;
  if (!name && !meta.managerOverrideAuthorizedBy) return null;
  const role =
    typeof meta.approvedByRole === 'string'
      ? prettyLabel(meta.approvedByRole)
      : typeof meta.authorizedByRole === 'string'
        ? prettyLabel(meta.authorizedByRole)
        : 'Manager';
  return `✓ Override approved by ${name ?? 'Manager'} (${role})`;
}

function actionSummary(entry: AuditLogEntry): string | null {
  const meta = entry.metadata ?? {};
  if (entry.action === 'discount.applied') {
    const pct = meta.discountPct != null ? `${meta.discountPct}%` : null;
    const amt = meta.discountAmount != null ? formatCurrency(Number(meta.discountAmount)) : null;
    const ticket = entry.entityLabel ? ` on ${entry.entityLabel}` : '';
    if (pct && amt) return `${pct} discount (${amt})${ticket}`;
    if (pct) return `${pct} discount${ticket}`;
    return amt ? `Discount ${amt}${ticket}` : null;
  }
  if (entry.action === 'ticket.voided') {
    return entry.entityLabel ? `${entry.entityLabel} voided` : 'Ticket voided';
  }
  if (entry.action === 'manager_override.approved') {
    const action = typeof meta.action === 'string' ? prettyLabel(meta.action) : 'Action';
    return `Manager override for ${action}`;
  }
  if (entry.action === 'manager_override.failed') {
    return 'Manager override PIN failed';
  }
  return null;
}

function AuditRow({ entry }: { entry: AuditLogEntry }) {
  const [open, setOpen] = useState(false);
  const hasDetail = Boolean(entry.beforeValue || entry.afterValue || entry.metadata);
  const summary = actionSummary(entry);
  const override = overrideLine(entry.metadata);

  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-slate-50"
      >
        {hasDetail ? (
          open ? (
            <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          ) : (
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
          )
        ) : (
          <span className="w-4" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-950">{prettyLabel(entry.action)}</span>
            {entry.entityType ? (
              <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {entry.entityType}
                {entry.entityLabel ? `: ${entry.entityLabel}` : ''}
              </span>
            ) : null}
          </div>
          {summary ? <p className="mt-0.5 text-sm text-slate-700">{summary}</p> : null}
          {override ? (
            <p className="mt-0.5 text-xs font-semibold text-emerald-700">{override}</p>
          ) : null}
          <p className="mt-0.5 text-xs text-slate-500">
            {entry.userName ?? 'System'} · {formatDateTime(entry.createdAt)}
            {entry.pinVerified ? ' · PIN verified' : ''}
          </p>
        </div>
      </button>
      {open && hasDetail ? (
        <div className="space-y-2 bg-slate-50 px-4 py-3 pl-11 text-xs">
          {entry.beforeValue ? (
            <div>
              <p className="font-bold text-slate-600">Before</p>
              <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-slate-800 ring-1 ring-slate-200">
                {JSON.stringify(entry.beforeValue, null, 2)}
              </pre>
            </div>
          ) : null}
          {entry.afterValue ? (
            <div>
              <p className="font-bold text-slate-600">After</p>
              <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-slate-800 ring-1 ring-slate-200">
                {JSON.stringify(entry.afterValue, null, 2)}
              </pre>
            </div>
          ) : null}
          {entry.metadata && Object.keys(entry.metadata).length > 0 ? (
            <div>
              <p className="font-bold text-slate-600">Metadata</p>
              <pre className="mt-1 overflow-x-auto rounded bg-white p-2 text-slate-800 ring-1 ring-slate-200">
                {JSON.stringify(entry.metadata, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export function AuditLogSettingsPage() {
  const user = useAuthStore((s) => s.user);
  const elevated = ['tenant_admin', 'platform_admin'].includes(user?.role ?? '');

  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
    enabled: elevated,
  });

  const params = useMemo(
    () => ({
      userId: userId || undefined,
      action: action || undefined,
      entityType: entityType || undefined,
      dateFrom: dateFrom ? new Date(`${dateFrom}T00:00:00`).toISOString() : undefined,
      dateTo: dateTo ? new Date(`${dateTo}T23:59:59`).toISOString() : undefined,
      page,
      pageSize: 25,
    }),
    [userId, action, entityType, dateFrom, dateTo, page],
  );

  const auditQuery = useQuery({
    queryKey: ['audit-log', params],
    queryFn: () => listAuditLog(params),
    enabled: elevated,
  });

  if (!elevated) {
    return <Navigate to="/settings/store" replace />;
  }

  const rows = auditQuery.data?.data ?? [];
  const total = auditQuery.data?.meta?.total ?? 0;
  const pageSize = auditQuery.data?.meta?.pageSize ?? 25;

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <ScrollText className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-bold text-slate-950">Audit Log</h2>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Immutable record of sensitive actions across your tenant. Visible to tenant admins only.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          <label className="block text-sm">
            <span className="font-medium text-slate-600">User</span>
            <select
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All users</option>
              {(usersQuery.data ?? []).map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-600">Action</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5"
              value={action}
              onChange={(e) => {
                setAction(e.target.value);
                setPage(1);
              }}
              placeholder="e.g. ticket.created"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-600">Entity type</span>
            <input
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5"
              value={entityType}
              onChange={(e) => {
                setEntityType(e.target.value);
                setPage(1);
              }}
              placeholder="e.g. ticket"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-600">From</span>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-slate-600">To</span>
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </label>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {auditQuery.isLoading ? (
          <p className="p-8 text-center text-slate-500">Loading audit log…</p>
        ) : rows.length === 0 ? (
          <p className="p-8 text-center text-slate-500">No audit entries match your filters.</p>
        ) : (
          rows.map((entry) => <AuditRow key={entry.id} entry={entry} />)
        )}
      </div>

      <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
    </div>
  );
}
